import React from 'react'
import clsx from 'clsx'
import Drawer from '@material-ui/core/Drawer'
import Hidden from '@material-ui/core/Hidden'
import List from '@material-ui/core/List'
import { makeStyles } from '@material-ui/core/styles'
import SidebarItem from './sidebar-item'
import { drawerWidth } from './styles'

// Drawer slot styling lives here as a makeStyles ruleset (rather than a
// styled() definition). MUI v4 in development mode generates hashed
// class names (e.g. `MuiDrawer-paper-1811`), so descendant selectors
// like `& .MuiDrawer-paper` do not match. The canonical MUI v4 way to
// style slots is via the component's `classes` prop, which is what we
// do here. When MUI v5/v6 lands, this can migrate to slot-aware
// `styled()` definitions in one place.
const useDrawerStyles = makeStyles(theme => ({
  drawer: {
    [theme.breakpoints.up('sm')]: {
      width: drawerWidth,
      flexShrink: 0,
      whiteSpace: 'nowrap',
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
    },
  },
  drawerContainerClosed: {
    [theme.breakpoints.up('sm')]: {
      width: theme.spacing(7) + 1,
    },
  },
  drawerPaper: ({ assumedUser }) => ({
    width: drawerWidth,
    marginTop: assumedUser ? '30px' : 'unset',
    [theme.breakpoints.only('xs')]: {
      marginTop: assumedUser ? '70px' : 'unset',
    },
  }),
  drawerOpen: ({ assumedUser }) => ({
    width: drawerWidth,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginTop: assumedUser ? '30px' : 'unset',
    [theme.breakpoints.only('xs')]: {
      marginTop: assumedUser ? '70px' : 'unset',
    },
  }),
  drawerClose: ({ assumedUser }) => ({
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: theme.spacing(7) + 1,
    marginTop: assumedUser ? '30px' : 'unset',
    [theme.breakpoints.only('xs')]: {
      marginTop: assumedUser ? '70px' : 'unset',
    },
    '& span': {
      opacity: '0',
      transition: theme.transitions.create('opacity', {
        easing: theme.transitions.easing.sharp,
        delay: theme.transitions.duration.leavingScreen,
        duration: '0s',
      }),
    },
  }),
  menu: {
    padding: 0,
  },
  toolbarSpacing: {
    ...theme.mixins.toolbar,
    minHeight: '61px',
  },
}))

export default function Sidebar({
  menuItems,
  isMobile,
  isTablet,
  isMobileNavOpen,
  isTabletNavOpen,
  setIsTabletNavOpen,
  handleDrawerToggle,
  assumedUser,
  drawerContainer,
  location,
}) {
  const classes = useDrawerStyles({ assumedUser })

  if (!menuItems.length) return null

  const renderMenu = () => (
    <List className={classes.menu}>
      {menuItems.map(item => {
        const ItemComponent = item?.component ? item.component : SidebarItem
        return (
          <ItemComponent
            {...{
              onClick: isMobile ? handleDrawerToggle : undefined,
              key: item.path || item.label,
              ...item,
              location,
              isTabletNavOpen,
              setIsTabletNavOpen,
            }}
          />
        )
      })}
    </List>
  )

  return (
    <nav
      className={`${classes.drawer} ${
        !isTabletNavOpen && isTablet ? classes.drawerContainerClosed : ''
      }`}
    >
      <Hidden smDown implementation="css">
        <Drawer
          color="primary"
          className={classes.drawer}
          variant="persistent"
          open
          classes={{ paper: classes.drawerPaper }}
        >
          <div className={classes.toolbarSpacing} />
          {renderMenu()}
        </Drawer>
      </Hidden>
      <Hidden xsUp implementation="css">
        <Drawer
          container={drawerContainer}
          variant="temporary"
          anchor="left"
          open={isMobileNavOpen}
          onClose={handleDrawerToggle}
          classes={{ paper: classes.drawerPaper }}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
        >
          {renderMenu()}
        </Drawer>
      </Hidden>
      <Hidden mdUp xsDown implementation="css">
        <Drawer
          variant="permanent"
          open={isTabletNavOpen}
          className={clsx(classes.drawer, {
            [classes.drawerOpen]: isTabletNavOpen,
            [classes.drawerClose]: !isTabletNavOpen,
          })}
          classes={{
            paper: clsx({
              [classes.drawerOpen]: isTabletNavOpen,
              [classes.drawerClose]: !isTabletNavOpen,
            }),
          }}
        >
          <div className={classes.toolbarSpacing} />
          {renderMenu()}
        </Drawer>
      </Hidden>
    </nav>
  )
}
